/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */
import { useCallback } from 'react';
import useLog from '../useLog';
import { fetchRawAPI } from '../../util';
import type {
  CanvasComponent,
  CanvasComponentConfig,
  CanvasContainerMode,
  CanvasWorkspace,
  SymbolGroup,
} from 'types/canvas';

interface WorkspaceSummary {
  id: number;
  name: string;
}

interface ContainerInput {
  id?: number;
  workspace_id: number;
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
  mode?: CanvasContainerMode;
}

interface CreateWorkspaceBody {
  name: string;
  layout?: Array<
    Omit<ContainerInput, 'id'> & {
      components?: Omit<CanvasComponent, 'id' | 'container_id'>[];
    }
  >;
}

interface SyncWorkspaceBody {
  name?: string;
  layout: ContainerInput[];
  // Optional. Omit to leave the workspace's existing groups untouched
  // (the backend reconciles stale ids against the post-sync layout
  // automatically). Send an explicit array — including [] — to overwrite.
  groups?: SymbolGroup[];
}

interface AddComponentBody {
  contentId: string;
  label: string;
  type: string;
  config?: CanvasComponentConfig;
  position_index?: number;
}

interface UpdateComponentBody {
  label: string;
  config: CanvasComponentConfig;
}

const isError = (response: any): boolean => response?.error != null;

const ensureComponentTypes = (c: any): CanvasComponent => ({
  ...c,
  id: Number(c.id),
  container_id: Number(c.container_id),
});

const ensureSummaryTypes = (s: any): WorkspaceSummary => ({
  ...s,
  id: Number(s.id),
});

// Defensive: backends sometimes serialize big-int ids as strings. The
// component-membership lookup is keyed on numeric ids, so silent string
// drift would collapse the sym-broadcast to a single-component update.
const ensureGroupTypes = (g: any): SymbolGroup => ({
  id: String(g.id),
  name: String(g.name),
  color: String(g.color),
  componentIds: Array.isArray(g.componentIds)
    ? g.componentIds.map((c: any) => Number(c)).filter(Number.isFinite)
    : [],
});

const ensureWorkspaceTypes = (workspace: any): CanvasWorkspace => ({
  ...workspace,
  id: Number(workspace.id),
  layout: (workspace.layout ?? []).map((container: any) => ({
    ...container,
    id: Number(container.id),
    workspace_id: Number(container.workspace_id),
    i: String(container.i),
    x: Number(container.x),
    y: Number(container.y),
    w: Number(container.w),
    h: Number(container.h),
    ...(container.minW != null ? { minW: Number(container.minW) } : {}),
    ...(container.maxW != null ? { maxW: Number(container.maxW) } : {}),
    ...(container.minH != null ? { minH: Number(container.minH) } : {}),
    ...(container.maxH != null ? { maxH: Number(container.maxH) } : {}),
    ...(container.mode === 'single' || container.mode === 'tabbed'
      ? { mode: container.mode }
      : {}),
    components: (container.components ?? []).map(ensureComponentTypes),
  })),
  groups: Array.isArray(workspace.groups)
    ? workspace.groups.map(ensureGroupTypes)
    : [],
});

export const useCanvasAPI = () => {
  const { fetchAPIWithLog } = useLog('useCanvasAPI');

  const fetchWorkspaces = useCallback(async (): Promise<
    WorkspaceSummary[] | null
  > => {
    const response = await fetchAPIWithLog<WorkspaceSummary[]>(
      'v1/me/canvas/workspaces',
    );
    if (isError(response)) {
      return null;
    }
    return (response as any[]).map(ensureSummaryTypes);
  }, [fetchAPIWithLog]);

  const fetchWorkspace = useCallback(
    async (id: number): Promise<CanvasWorkspace | null> => {
      const response = await fetchAPIWithLog<CanvasWorkspace>(
        `v1/me/canvas/workspaces/${id}`,
      );
      if (isError(response)) {
        return null;
      }
      return ensureWorkspaceTypes(response);
    },
    [fetchAPIWithLog],
  );

  const createWorkspace = useCallback(
    async (
      name: string,
      layout?: CreateWorkspaceBody['layout'],
    ): Promise<CanvasWorkspace | WorkspaceSummary | null> => {
      const body: CreateWorkspaceBody = { name };
      if (layout && layout.length > 0) {
        body.layout = layout;
      }
      const response = await fetchAPIWithLog<
        CanvasWorkspace | WorkspaceSummary
      >('v1/me/canvas/workspaces', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (isError(response)) {
        return null;
      }
      // Full-create returns a workspace; empty-create returns just {id, name}
      return 'layout' in (response as object)
        ? ensureWorkspaceTypes(response)
        : ensureSummaryTypes(response);
    },
    [fetchAPIWithLog],
  );

  const syncWorkspaceLayout = useCallback(
    async (
      id: number,
      name: string | undefined,
      layout: ContainerInput[],
      groups?: SymbolGroup[],
    ): Promise<CanvasWorkspace | null> => {
      const body: SyncWorkspaceBody = { layout };
      if (name != null) {
        body.name = name;
      }
      if (groups != null) {
        body.groups = groups;
      }
      const response = await fetchAPIWithLog<CanvasWorkspace>(
        `v1/me/canvas/workspaces/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        },
      );
      if (isError(response)) {
        return null;
      }
      return ensureWorkspaceTypes(response);
    },
    [fetchAPIWithLog],
  );

  const deleteWorkspace = useCallback(async (id: number): Promise<boolean> => {
    const response = await fetchRawAPI(`v1/me/canvas/workspaces/${id}`, {
      method: 'DELETE',
    });
    return !isError(response);
  }, []);

  const addComponent = useCallback(
    async (
      containerId: number,
      component: AddComponentBody,
    ): Promise<CanvasComponent | null> => {
      const response = await fetchAPIWithLog<CanvasComponent>(
        `v1/me/canvas/containers/${containerId}/components`,
        {
          method: 'POST',
          body: JSON.stringify(component),
        },
      );
      if (isError(response)) {
        return null;
      }
      return ensureComponentTypes(response);
    },
    [fetchAPIWithLog],
  );

  const updateComponent = useCallback(
    async (
      containerId: number,
      componentId: number,
      data: UpdateComponentBody,
    ): Promise<CanvasComponent | null> => {
      const response = await fetchAPIWithLog<CanvasComponent>(
        `v1/me/canvas/containers/${containerId}/components/${componentId}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        },
      );
      if (isError(response)) {
        return null;
      }
      return ensureComponentTypes(response);
    },
    [fetchAPIWithLog],
  );

  const deleteComponent = useCallback(
    async (containerId: number, componentId: number): Promise<boolean> => {
      const response = await fetchRawAPI(
        `v1/me/canvas/containers/${containerId}/components/${componentId}`,
        { method: 'DELETE' },
      );
      return !isError(response);
    },
    [],
  );

  return {
    fetchWorkspaces,
    fetchWorkspace,
    createWorkspace,
    syncWorkspaceLayout,
    deleteWorkspace,
    addComponent,
    updateComponent,
    deleteComponent,
  };
};

export type { WorkspaceSummary, ContainerInput };
